import { useEffect, useState } from 'react';

const SPLASH_DURATION = 6000; // ms — aligns with one full animation cycle

export function SplashScreen({ onFinish }: { onFinish: () => void }) {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeOut(true);
      setTimeout(onFinish, 400); // wait for fade-out transition
    }, SPLASH_DURATION);
    return () => clearTimeout(timer);
  }, [onFinish]);

  return (
    <div
      className="fixed inset-0 z-[9999] transition-opacity duration-400"
      style={{ opacity: fadeOut ? 0 : 1 }}
    >
      <style>{splashStyles}</style>
      <div className="stage">
        
        <div className="stars">
          <div className="star" style={{ top: '12%', left: '18%', animationDelay: '0s' }} />
          <div className="star" style={{ top: '8%', left: '72%', animationDelay: '0.6s' }} />
          <div className="star" style={{ top: '22%', left: '88%', animationDelay: '1.2s' }} />
          <div className="star" style={{ top: '32%', left: '8%', animationDelay: '0.3s' }} />
          <div className="star" style={{ top: '16%', left: '45%', animationDelay: '1.8s', width: '3px', height: '3px' }} />
          <div className="star" style={{ top: '6%', left: '30%', animationDelay: '2.1s' }} />
          <div className="star" style={{ top: '28%', left: '60%', animationDelay: '0.9s' }} />
          <div className="star" style={{ top: '14%', left: '92%', animationDelay: '1.5s' }} />
        </div>
        <div className="cloud cloud-1" />
        <div className="cloud cloud-2" />
        <div className="cloud cloud-3" />
        <div className="cloud cloud-4" />
        <div className="trail" />
        <div className="scene">
          <div className="plane-wrap">
            <svg className="plane" viewBox="0 -4.15 57.875 57.875" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(-1209.722 -1357.465)">
                <path d="M1224.729,1387.963v16.4l26.032-28.734Z" fill="#d1d3d4" stroke="#231f20" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" />
                <path d="M1228.118,1390.522l37.479-30.686-17.1,45.207Z" fill="#ffffff" stroke="#231f20" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" />
                <path d="M1211.722,1378.673l16.4,11.712,37.479-30.92Z" fill="#ffffff" stroke="#231f20" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" />
              </g>
            </svg>
            <div className="coins">
              <svg className="coin coin-1" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="26" fill="#f5e680" />
                <circle cx="32" cy="32" r="20" fill="#f2c94c" stroke="#d99e2b" strokeWidth="1.5" />
                <circle cx="32" cy="32" r="14" fill="none" stroke="#d99e2b" strokeWidth="1.2" strokeDasharray="2 3" />
                <text x="32" y="40" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="800" fontSize="22" fill="#7a5a10">$</text>
              </svg>
              <svg className="coin coin-2" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="26" fill="#f5e680" />
                <circle cx="32" cy="32" r="20" fill="#f2c94c" stroke="#d99e2b" strokeWidth="1.5" />
                <circle cx="32" cy="32" r="14" fill="none" stroke="#d99e2b" strokeWidth="1.2" strokeDasharray="2 3" />
                <text x="32" y="40" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="800" fontSize="22" fill="#7a5a10">$</text>
              </svg>
              <svg className="coin coin-3" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="26" fill="#f5e680" />
                <circle cx="32" cy="32" r="20" fill="#f2c94c" stroke="#d99e2b" strokeWidth="1.5" />
                <circle cx="32" cy="32" r="14" fill="none" stroke="#d99e2b" strokeWidth="1.2" strokeDasharray="2 3" />
                <text x="32" y="40" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="800" fontSize="22" fill="#7a5a10">$</text>
              </svg>
            </div>
          </div>
        </div>
        <div className="horizon" />
        <div className="wordmark">
          <img src="/MONIGO_LOGO_WHITE.png" alt="MoniGo" className="h-auto w-48 sm:w-56" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.4))' }} />
        </div>
      </div>
    </div>
  );
}

const splashStyles = `
:root {
  --sky-deep:   #0b1e3a;
  --sky-mid:    #1e3a6b;
  --sky-glow:   #ff8a5b;
  --sky-warm:   #ffc266;
  --gold:       #f2c94c;
  --gold-deep:  #d99e2b;
  --ink:        #0a1525;
  --paper:      #fff9ec;
  --cloud:      rgba(255, 249, 236, 0.85);
}
.stage {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(ellipse 80% 60% at 70% 90%, rgba(255, 138, 91, 0.45) 0%, transparent 60%),
    radial-gradient(ellipse 70% 50% at 30% 100%, rgba(255, 194, 102, 0.35) 0%, transparent 55%),
    linear-gradient(180deg, var(--sky-deep) 0%, var(--sky-mid) 55%, #2d4a7a 100%);
}
.stage::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.4 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
  opacity: 0.08;
  pointer-events: none;
  mix-blend-mode: overlay;
  z-index: 50;
}
.stage::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%);
  pointer-events: none;
  z-index: 40;
}
.stars {
  position: absolute;
  inset: 0;
  z-index: 1;
}
.star {
  position: absolute;
  width: 2px;
  height: 2px;
  background: #fff9ec;
  border-radius: 50%;
  opacity: 0;
  box-shadow: 0 0 4px rgba(255, 249, 236, 0.8);
  animation: twinkle 3s ease-in-out infinite;
}
@keyframes twinkle {
  0%, 100% { opacity: 0.2; transform: scale(0.8); }
  50%      { opacity: 0.9; transform: scale(1.1); }
}
.cloud {
  position: absolute;
  background: var(--cloud);
  border-radius: 100px;
  filter: blur(8px);
  opacity: 0;
  z-index: 2;
}
.cloud-1 { width: 140px; height: 30px; top: 28%; left: -160px; animation: drift 7s linear infinite; animation-delay: 0.3s; }
.cloud-2 { width: 90px; height: 22px; top: 65%; left: -120px; filter: blur(6px); animation: drift 9s linear infinite; animation-delay: 1.4s; }
.cloud-3 { width: 180px; height: 36px; top: 78%; left: -200px; filter: blur(10px); animation: drift 8s linear infinite; animation-delay: 2.2s; }
.cloud-4 { width: 70px; height: 18px; top: 18%; left: -100px; filter: blur(5px); animation: drift 6s linear infinite; animation-delay: 3s; }
@keyframes drift {
  0%   { opacity: 0; transform: translateX(0); }
  15%  { opacity: 0.55; }
  85%  { opacity: 0.55; }
  100% { opacity: 0; transform: translateX(calc(100vw + 220px)); }
}
.scene {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  z-index: 10;
}
.plane-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: planeFlyIn 6s ease-out infinite;
}
@keyframes planeFlyIn {
  0%   { transform: translate(-180%, 80%) rotate(-8deg); opacity: 0; }
  18%  { opacity: 1; }
  42%  { transform: translate(0, 0) rotate(0deg); opacity: 1; }
  62%  { transform: translate(0, -6px) rotate(0deg); opacity: 1; }
  82%  { transform: translate(0, 0) rotate(0deg); opacity: 1; }
  100% { transform: translate(180%, -60%) rotate(8deg); opacity: 0; }
}
.plane {
  width: 200px;
  height: 200px;
  filter: drop-shadow(0 18px 24px rgba(0, 0, 0, 0.45));
  animation: planeBob 2.4s ease-in-out infinite;
}
@keyframes planeBob {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50%      { transform: translateY(-6px) rotate(2deg); }
}
.coins { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
.coin {
  position: absolute;
  width: 60px; height: 60px;
  filter: drop-shadow(0 6px 10px rgba(217, 158, 43, 0.5));
  opacity: 0;
  animation-iteration-count: infinite;
  animation-duration: 6s;
  animation-timing-function: ease-out;
}
.coin-1 { top: 18px; left: 50%; margin-left: -30px; animation-name: coinDrop1; animation-delay: 0s; }
.coin-2 { top: 28px; left: 50%; margin-left: -64px; width: 44px; height: 44px; animation-name: coinDrop2; animation-delay: 0.1s; }
.coin-3 { top: 32px; left: 50%; margin-left: 16px; width: 40px; height: 40px; animation-name: coinDrop3; animation-delay: 0.18s; }
@keyframes coinDrop1 {
  0%   { opacity: 0; transform: translateY(-180px) rotate(-30deg) scale(0.6); }
  35%  { opacity: 0; }
  42%  { opacity: 1; transform: translateY(-40px) rotate(0deg) scale(1); }
  48%  { transform: translateY(0) rotate(8deg) scale(1.05); }
  54%  { transform: translateY(-8px) rotate(-4deg) scale(1); }
  62%  { transform: translateY(0) rotate(0deg) scale(1); }
  82%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-20px) rotate(0deg) scale(0.9); }
}
@keyframes coinDrop2 {
  0%   { opacity: 0; transform: translateY(-200px) rotate(40deg) scale(0.5); }
  38%  { opacity: 0; }
  46%  { opacity: 1; transform: translateY(-30px) rotate(0deg) scale(1); }
  52%  { transform: translateY(2px) rotate(-12deg) scale(1.05); }
  58%  { transform: translateY(-6px) rotate(6deg) scale(1); }
  64%  { transform: translateY(0) rotate(0deg) scale(1); }
  82%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-20px) rotate(0deg) scale(0.9); }
}
@keyframes coinDrop3 {
  0%   { opacity: 0; transform: translateY(-220px) rotate(-50deg) scale(0.5); }
  40%  { opacity: 0; }
  48%  { opacity: 1; transform: translateY(-25px) rotate(0deg) scale(1); }
  54%  { transform: translateY(3px) rotate(14deg) scale(1.05); }
  60%  { transform: translateY(-5px) rotate(-7deg) scale(1); }
  66%  { transform: translateY(0) rotate(0deg) scale(1); }
  82%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-20px) rotate(0deg) scale(0.9); }
}
.trail {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 6px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 9;
  opacity: 0;
  animation: trailFade 6s ease-out infinite;
}
.trail::before {
  content: '';
  position: absolute;
  top: 0; left: 0;
  height: 100%; width: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 249, 236, 0.6) 20%, rgba(255, 249, 236, 0.9) 50%, rgba(255, 249, 236, 0.6) 80%, transparent 100%);
  filter: blur(3px);
  border-radius: 4px;
}
@keyframes trailFade {
  0%   { opacity: 0; transform: translateY(-50%) scaleX(0); transform-origin: left; }
  25%  { opacity: 0.7; transform: translateY(-50%) scaleX(0.6); transform-origin: left; }
  42%  { opacity: 0.4; transform: translateY(-50%) scaleX(1); transform-origin: left; }
  65%  { opacity: 0; }
  100% { opacity: 0; }
}
.wordmark {
  position: absolute;
  left: 50%;
  bottom: 18%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 20;
  opacity: 0;
  animation: wordmarkIn 6s ease-out infinite;
}

@keyframes wordmarkIn {
  0%, 60%  { opacity: 0; transform: translate(-50%, 12px); }
  72%      { opacity: 1; transform: translate(-50%, 0); }
  90%      { opacity: 1; transform: translate(-50%, 0); }
  100%     { opacity: 0; transform: translate(-50%, -6px); }
}
.horizon {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent 0%, var(--gold) 50%, transparent 100%);
  opacity: 0.6;
  z-index: 5;
  filter: blur(1px);
}
@media (min-width: 480px) {
  .scene { width: 340px; height: 340px; }
  .plane { width: 240px; height: 240px; }
  .wordmark h1 { font-size: 64px; }
}
`;
